/**
 * @file        src/components/PasswordField.jsx
 * @description The bordered password row used by the corporate auth flow — lock
 *              glyph, the input itself, and a reveal ("show password") toggle on
 *              the right. Replaces the hand-rolled
 *              `<div class="field …"><svg/><input type="password"/></div>` block
 *              so every password field in the funnel has the same markup, the
 *              same a11y and the same icon.
 *
 *              Every prop is forwarded to the real input (`value`, `onChange`,
 *              `onKeyDown`, `autoComplete`, `placeholder`, `minLength`…) and the
 *              ref points at the input, not the wrapper, so AuthModal can still
 *              focus it on arrival. The ONLY thing owned here is the `type`,
 *              flipping between 'password' and 'text'.
 *
 *              Nothing is stored, echoed or logged: revealing is a per-field,
 *              per-mount, user-initiated act that resets when the modal closes.
 * @author      Kinetika Labs
 * @created     2026-09-16
 * @changelog
 *   - 2026-09-16 Claude — Initial. The corporate login's password was type-only,
 *                          so a customer who mistyped had no way to check it and
 *                          only found out from a rejected login — which reads the
 *                          same as a wrong account.
 *   - 2026-09-18 Claude — `lock`, `rowSkin` and `inputSkin` props, so the two
 *                          remaining type-only password inputs (corporate
 *                          signup, the Wallet employee form) can reuse this
 *                          instead of hand-rolling a third reveal toggle. Their
 *                          sibling inputs carry no icon and, in the Wallet form,
 *                          a smaller box — defaults are unchanged, so AuthModal
 *                          renders exactly the markup it did before.
 */
import React, { forwardRef, useState } from 'react';

// Filled 24×24, matching the storefront's shared glyph family (the 2026-08-18
// icon refresh) rather than the stroke set the admin console uses.
const LOCK =
  'M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5m0 2a3 3 0 0 1 3 3v3H9V6a3 3 0 0 1 3-3m0 11a2 2 0 0 1 1 3.7V20h-2v-2.3A2 2 0 0 1 12 14';
const EYE =
  'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5m0 12.5a5 5 0 1 1 0-10 5 5 0 0 1 0 10m0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6';
const EYE_OFF =
  'M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7M2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.31-.84l.43.43L19.73 22 21 20.73zm5.53 5.53 1.55 1.55c-.05.21-.08.43-.08.65a3 3 0 0 0 3 3c.22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53a5 5 0 0 1-5-5c0-.79.2-1.53.53-2.2m4.31-.78 3.15 3.15.02-.16a3 3 0 0 0-3-3z';

// The row and the input are each split into the part that is STRUCTURAL (always
// applied) and the part that is a SKIN (replaceable). The default skin is the
// corporate auth funnel's field box, so a password row sits flush with the email
// and OTP rows beside it. Screens whose inputs are shaped differently — the
// Wallet page's employee form is a smaller `px-3 py-2 rounded-lg` box — pass
// their own `rowSkin`/`inputSkin` instead of growing a second copy of this
// component. `field` is load-bearing in the structural half: global.css's
// `.field:focus-within` paints the brand's focus ring, so a caller that swaps
// the skin still gets it (and gets its OWN brand's colour, Neon or Beyond).
const ROW_STRUCTURE = 'field flex items-center gap-2';
const ROW_SKIN = 'bg-night/70 rounded-xl border border-line px-4 py-2.5';
const INPUT_STRUCTURE = 'w-full bg-transparent outline-none';
const INPUT_SKIN = 'text-[15px] placeholder:text-muted/50';

/**
 * @param {boolean} [lock=true]      render the lock glyph. Turn it OFF where the
 *                                   sibling inputs carry no icons — a lone glyph
 *                                   singles the password field out instead of
 *                                   matching the form it sits in.
 * @param {string}  [rowSkin]        replaces the row's padding/radius/border/background.
 * @param {string}  [inputSkin]      replaces the input's type styling.
 * @param {string}  [className]      extra classes on the bordered row.
 * @param {string}  [inputClassName] extra classes on the input itself.
 */
const PasswordField = forwardRef(function PasswordField(
  { className = '', inputClassName = '', rowSkin = ROW_SKIN, inputSkin = INPUT_SKIN, lock = true, ...rest },
  ref,
) {
  const [shown, setShown] = useState(false);
  const label = shown ? 'Hide password' : 'Show password';

  return (
    <div className={`${ROW_STRUCTURE} ${rowSkin} ${className}`}>
      {lock ? <svg viewBox="0 0 24 24" className="w-4 h-4 text-muted shrink-0"><path fill="currentColor" d={LOCK} /></svg> : null}
      <input
        ref={ref}
        type={shown ? 'text' : 'password'}
        className={`${INPUT_STRUCTURE} ${inputSkin} ${inputClassName}`}
        {...rest}
      />
      {/* onMouseDown is prevented so the click never pulls focus out of the
          input — the caret stays where it was and typing can continue straight
          after a peek. type="button" keeps it from submitting any enclosing form. */}
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setShown((s) => !s)}
        aria-label={label}
        aria-pressed={shown}
        title={label}
        className="shrink-0 -mr-1.5 p-1.5 rounded-lg text-muted hover:text-ink cursor-pointer transition"
      >
        <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] block"><path fill="currentColor" d={shown ? EYE_OFF : EYE} /></svg>
      </button>
    </div>
  );
});

export default PasswordField;
